import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";

/**
 * Invisible component. Mounted in Layout. Posts a 30s heartbeat to the
 * backend every 30s while the tab is visible, tagged with the current
 * section derived from the route path.
 *
 * Section taxonomy (matches backend SECTIONS):
 *   /programs/.../upload | /validate    → "ingestion"
 *   /programs/.../report                → "reporting"
 *   /programs/.../theory-of-change      → "reporting"
 *   /forms*                             → "forms"
 *   /programs* (all other)              → "programs"
 *   /                                   → "dashboard"
 *   anything else                       → "other"
 */
function classifySection(path) {
  if (path === "/" || path.startsWith("/admin")) return "dashboard";
  if (path.includes("/upload") || path.includes("/validate")) return "ingestion";
  if (path.includes("/report") || path.includes("/theory-of-change")) return "reporting";
  if (path.includes("/forms")) return "forms";
  if (path.startsWith("/programs")) return "programs";
  if (path.startsWith("/my-analytics") || path.startsWith("/key-dictionary")) return "dashboard";
  return "other";
}

const HEARTBEAT_MS = 30_000;

export default function HeartbeatTracker() {
  const { user } = useAuth();
  const location = useLocation();
  const sectionRef = useRef(classifySection(location.pathname));
  const lastTickRef = useRef(Date.now());
  const accumulatedRef = useRef(0);

  // Keep the section pointer fresh on every navigation.
  useEffect(() => {
    sectionRef.current = classifySection(location.pathname);
    lastTickRef.current = Date.now();
  }, [location.pathname]);

  useEffect(() => {
    if (!user) return undefined;
    let timer;
    const flush = async () => {
      const now = Date.now();
      if (document.visibilityState !== "visible") {
        lastTickRef.current = now;
        return;
      }
      const elapsed = now - lastTickRef.current;
      lastTickRef.current = now;
      accumulatedRef.current += elapsed;
      if (accumulatedRef.current >= HEARTBEAT_MS) {
        const duration = Math.min(accumulatedRef.current, 120_000);
        accumulatedRef.current = 0;
        try {
          await api.post("/me/heartbeat", { section: sectionRef.current, duration_ms: duration });
        } catch {
          /* swallow — heartbeats are best-effort */
        }
      }
    };
    timer = setInterval(flush, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [user]);

  return null;
}
